import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { filterArrayEmpty } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doSetupOrganisationSave } from 'store/actions';
import { SUPPORT_EMAIL } from 'types/types';

export const OrganisationDetailsModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsOrganisationDetailsModal>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { editName, editAddress, editPhone, editWebsite, editApproveRequires } =
    route.params.userFeatures;

  const initValues = React.useRef({
    name: route.params.name,
    address1: route.params.address1,
    address2: route.params.address2,
    city: route.params.city,
    county: route.params.county,
    postcode: route.params.postcode,
    phone: route.params.phone,
    website: route.params.website,
    approveRequireAddress: route.params.approveRequireAddress,
    approveRequireLogo: route.params.approveRequireLogo,
    approveRequirePhone: route.params.approveRequirePhone,
    approveRequireWebsite: route.params.approveRequireWebsite,
  });
  const changes = React.useRef(cloneDeep(initValues.current));

  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
      doForceUpdate?: boolean,
    ) => {
      changes.current[name] = value;
      if (doForceUpdate || isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (isDirty.current) {
      dispatch(
        doSetupOrganisationSave({
          changes: {
            address: {
              name: changes.current.name,
              address1: changes.current.address1,
              address2: changes.current.address2,
              city: changes.current.city,
              county: changes.current.county,
              postcode: changes.current.postcode,
            },
            phone: changes.current.phone,
            website: changes.current.website,
            approveRequireAddress: changes.current.approveRequireAddress,
            approveRequireLogo: changes.current.approveRequireLogo,
            approveRequirePhone: changes.current.approveRequirePhone,
            approveRequireWebsite: changes.current.approveRequireWebsite,
          },
        }),
      );
      return;
    }
    dispatch(
      NAVIGATE_MODAL_BACK([ModalScreens.SettingsOrganisationDetailsModal]),
    );
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Details"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      {editName ? (
        <Card label="Name">
          <TextField
            placeholder="..."
            value={changes.current.name}
            onChangeWithName={handleChangeWithName}
            name="name"
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
            autoFocusWeb
            autoFocusDelay
            rbs
          />
        </Card>
      ) : (
        <Card
          label="Name"
          explanation={smartText(
            `Please contact ${SUPPORT_EMAIL} to change your organisation's name`,
          )}
        >
          <CardRow value={changes.current.name} />
        </Card>
      )}

      {editAddress ? (
        <Card label="Address">
          <TextField
            placeholder="Address 1"
            value={changes.current.address1}
            name="address1"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
          <TextField
            placeholder="Address 2"
            value={changes.current.address2}
            name="address2"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
          <TextField
            placeholder="City"
            value={changes.current.city}
            name="city"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
          <TextField
            placeholder="County"
            value={changes.current.county}
            name="county"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
          <TextField
            placeholder="Postcode"
            value={changes.current.postcode}
            name="postcode"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
        </Card>
      ) : (
        <Card label="Address">
          <CardRow
            placeholder="No Address"
            value={[
              changes.current.address1,
              changes.current.address2,
              changes.current.city,
              changes.current.county,
              changes.current.postcode,
            ]
              .filter(filterArrayEmpty)
              .join('\n')}
          />
        </Card>
      )}

      <Card label="Phone">
        {editPhone ? (
          <TextField
            placeholder="No Phone"
            value={changes.current.phone}
            name="phone"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
            maxLength={50}
          />
        ) : (
          <CardRow placeholder="No Phone" value={changes.current.phone} />
        )}
      </Card>

      <Card label="Website">
        {editWebsite ? (
          <TextField
            placeholder="No Website"
            value={changes.current.website}
            name="website"
            onChangeWithName={handleChangeWithName}
            autoCapitalize="words"
            autoCorrect={false}
            onEsc={handleClose}
          />
        ) : (
          <CardRow placeholder="No Website" value={changes.current.website} />
        )}
      </Card>

      <Card
        label="To Approve Assessments"
        explanation="A billing address is always required to approve assessments"
      >
        <ViewWithSeparators horizontal>
          <CardRow
            label="Address Required"
            checkedValue={changes.current.approveRequireAddress}
            style={{ flex: 1 }}
            onPress={
              editApproveRequires
                ? () =>
                    handleChangeWithName(
                      'approveRequireAddress',
                      !changes.current.approveRequireAddress,
                      true,
                    )
                : undefined
            }
          />
          <CardRow
            label="Logo Required"
            checkedValue={changes.current.approveRequireLogo}
            style={{ flex: 1 }}
            onPress={
              editApproveRequires
                ? () =>
                    handleChangeWithName(
                      'approveRequireLogo',
                      !changes.current.approveRequireLogo,
                      true,
                    )
                : undefined
            }
          />
        </ViewWithSeparators>
        <ViewWithSeparators horizontal>
          <CardRow
            label="Phone Required"
            checkedValue={changes.current.approveRequirePhone}
            style={{ flex: 1 }}
            onPress={
              editApproveRequires
                ? () =>
                    handleChangeWithName(
                      'approveRequirePhone',
                      !changes.current.approveRequirePhone,
                      true,
                    )
                : undefined
            }
          />
          <CardRow
            label="Website Required"
            checkedValue={changes.current.approveRequireWebsite}
            style={{ flex: 1 }}
            onPress={
              editApproveRequires
                ? () =>
                    handleChangeWithName(
                      'approveRequireWebsite',
                      !changes.current.approveRequireWebsite,
                      true,
                    )
                : undefined
            }
          />
        </ViewWithSeparators>
      </Card>
    </PageScrollView>
  );
};
